import {useState} from 'react'
import {ViewMode} from './CommentsContext'

export const CommentsSort = {
  NewestFirst: 'newest-first',
  OldestFirst: 'oldest-first',
  DiffOrder: 'diff-order',
} as const

export type CommentsSort = (typeof CommentsSort)[keyof typeof CommentsSort]

/**
 * The state values of the comments filter
 */
export interface CommentsFilterState {
  hideResolvedThreads: boolean
  hideOutdatedThreads: boolean
  filterText: string
  selectedAuthor?: string
  /**
   * Sort controls both the direction of the items and how they are displayed:
   *  - `NewestFirst` and `OldestFirst` render review threads under review summaries along with issue comments
   *  - `DiffOrder` renders flat list of review threads, because review summaries and issue comments have no diff position
   */
  sort: CommentsSort
}

export const defaultFilterState = {
  filterText: '',
  hideResolvedThreads: true,
  hideOutdatedThreads: false,
  sort: CommentsSort.DiffOrder,
} as const

interface UseCommentsFilterStateResult {
  filterState: CommentsFilterState
  /** Set the filter state for the current view mode. */
  setCurrentFilterState: React.Dispatch<React.SetStateAction<CommentsFilterState>>
  /** Set the filter state for a specific view mode, even if it is not the current mode. */
  setViewModeFilterState: (viewMode: ViewMode, filterState: React.SetStateAction<CommentsFilterState>) => void
}

/** Tracks the state of comments filters. */
export function useCommentsFilterState(viewMode: ViewMode): UseCommentsFilterStateResult {
  // filter state for each view mode is tracked separately
  const [threadPreviewFilterState, setThreadPreviewFilterState] = useState<CommentsFilterState>({
    ...defaultFilterState,
  })
  const [issueCommentsFilterState, setIssueCommentsFilterState] = useState<CommentsFilterState>({
    ...defaultFilterState,
    sort: CommentsSort.OldestFirst,
  })

  const setViewModeFilterState = (mode: ViewMode, filterState: React.SetStateAction<CommentsFilterState>) => {
    if (mode === ViewMode.ThreadPreviews) {
      setThreadPreviewFilterState(filterState)
    } else if (mode === ViewMode.IssueComments) {
      setIssueCommentsFilterState(filterState)
    }
  }

  const setCurrentFilterState = (filterState: React.SetStateAction<CommentsFilterState>) =>
    setViewModeFilterState(viewMode, filterState)

  return {
    filterState: viewMode === ViewMode.ThreadPreviews ? threadPreviewFilterState : issueCommentsFilterState,
    setCurrentFilterState,
    setViewModeFilterState,
  } as const
}
